import { useState } from "react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { CheckCircle, ArrowClockwise, Copy, Check, Globe } from "@phosphor-icons/react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : null);

function CopyValue({ value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard unavailable */ }
  };
  return (
    <span className="inline-flex items-center gap-1 min-w-0">
      <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-[10px] break-all" data-testid={testid}>{value}</code>
      <button type="button" onClick={copy} className="text-muted-foreground hover:text-foreground" title="Copy" data-testid={`${testid}-copy`}>
        {copied ? <Check size={11} className="text-emerald-600" /> : <Copy size={11} />}
      </button>
    </span>
  );
}

export default function DomainSetupWizard({ domain, info, verified, verifying, onVerify }) {
  const txtHost = info?.txt_host || `_fixlr-verify.${domain}`;
  const txtValue = info?.txt_value || info?.verification_record || "";
  const cname = info?.cname_target || "app.fixlr.app";
  const steps = [
    { n: 1, title: "Point your domain at Fixlr", body: <>Add a <b>CNAME</b> record: <CopyValue value={domain} testid="domain-cname-host" /> → <CopyValue value={cname} testid="domain-cname-target" /></> },
    { n: 2, title: "Prove you own it", body: <>Add a <b>TXT</b> record at <CopyValue value={txtHost} testid="domain-txt-host" /> with the value <CopyValue value={txtValue} testid="domain-txt-value" /></> },
    { n: 3, title: "Check", body: <>DNS changes usually show up within minutes but can take up to an hour. Press <b>Check again</b> whenever you like — we'll turn the badge green as soon as the record is found.</> },
  ];
  return (
    <div className="space-y-3" data-testid="domain-setup-wizard">
      <div className="border border-border rounded-md p-3 bg-muted/30">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold flex items-center gap-1.5"><Globe size={12} /> Connect {domain}</p>
          {verified ? (
            <Badge className="text-[9px] bg-emerald-100 text-emerald-700 gap-1" data-testid="domain-verified-badge">
              <CheckCircle size={9} weight="fill" /> Verified{info?.domain_verified_at ? ` · ${fmt(info.domain_verified_at)}` : ""}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[9px] gap-1 border-amber-400 text-amber-700" data-testid="domain-pending-badge">Awaiting DNS record</Badge>
          )}
        </div>
        <ol className="space-y-2">
          {steps.map((s) => (
            <li key={s.n} className={`flex gap-2 text-[11px] leading-relaxed ${verified && s.n === 2 ? "opacity-60" : ""}`} data-testid={`domain-step-${s.n}`}>
              <span className={`shrink-0 h-4 w-4 rounded-full text-[9px] font-bold flex items-center justify-center ${verified && s.n !== 3 ? "bg-emerald-600 text-white" : "bg-foreground text-background"}`}>{verified && s.n !== 3 ? "✓" : s.n}</span>
              <div className="min-w-0"><p className="font-semibold">{s.title}</p><p className="text-muted-foreground">{s.body}</p></div>
            </li>
          ))}
        </ol>
      </div>
      <div className="flex items-center gap-3">
        <Button size="sm" variant={verified ? "ghost" : "outline"} onClick={onVerify} disabled={verifying} className="text-xs" data-testid="verify-domain-btn">
          <ArrowClockwise size={12} className={`mr-1 ${verifying ? "animate-spin" : ""}`} />
          {verifying ? "Checking DNS…" : verified ? "Check again" : "Check DNS now"}
        </Button>
        {info?.domain_last_checked_at && <span className="text-[10px] text-muted-foreground" data-testid="domain-last-checked">Last checked {fmt(info.domain_last_checked_at)}</span>}
      </div>
    </div>
  );
}
